import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/InvoiceLayout";
import config from "../config.js";
import { useSelector } from "react-redux";

const CustomerManagement = () => {
  // Initialize customers as an empty array to prevent map errors
  const [customers, setCustomers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  const [formData, setFormData] = useState({
    customerId: "",
    name: "",
    email: "",
    phone: "",
    address: { city: "", state: "", postalCode: "" },
    customerSegment: "B2B",
    paymentId: "",
    purchaseDate: "",
    productPurchased: "",
    amountspend: "",
    paymentMethod: "",
  });

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCustomers();
    }
  }, [selectedCompanyId]);

  const fetchCustomers = async () => {
    if (!selectedCompanyId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/customer/${selectedCompanyId}`
      );
      
      // Ensure customers is always an array
      const customerData = response.data && response.data.data 
        ? response.data.data 
        : response.data && Array.isArray(response.data) 
          ? response.data 
          : [];
          
      setCustomers(customerData);
      
      if (customerData.length > 0) {
        setSnackbar({
          open: true,
          message: "Customers loaded successfully.",
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      setSnackbar({
        open: true,
        message: `Failed to load customers: ${err.message || "Unknown error"}`,
        severity: "error",
      });
      // Always set customers to empty array on error
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    try {
      if (selectedCustomer) {
        await axios.put(
          `${API_BASE_URL}/api/v1/customer/${selectedCustomer._id}`,
          formData
        );
        setSnackbar({
          open: true,
          message: "Customer updated successfully.",
          severity: "success",
        });
      } else {
        const newCustomer = {
          ...formData,
          customerId: generateUniqueCustomerId(),
          kycId: selectedCompanyId,
        };
        await axios.post(
          `${API_BASE_URL}/api/v1/customer/${selectedCompanyId}`,
          newCustomer
        );
        setSnackbar({
          open: true,
          message: "Customer added successfully.",
          severity: "success",
        });
      }
      fetchCustomers();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save customer:", err);
      setSnackbar({
        open: true,
        message: `Failed to save customer: ${err.message || "Unknown error"}`,
        severity: "error",
      });
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setFormData({
        ...customer,
        address: customer.address || { city: "", state: "", postalCode: "" },
      });
      setSelectedCustomer(customer);
    } else {
      setFormData({
        customerId: "",
        name: "",
        email: "",
        phone: "",
        address: { city: "", state: "", postalCode: "" },
        customerSegment: "B2B",
        paymentId: "",
        purchaseDate: "",
        productPurchased: "",
        amountspend: "",
        paymentMethod: "",
      });
      setSelectedCustomer(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        address: { ...prevData.address, [addressField]: value },
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/customer/${customerId}`);
      setSnackbar({
        open: true,
        message: "Customer deleted successfully.",
        severity: "success",
      });
      fetchCustomers();
    } catch (err) {
      console.error("Failed to delete customer:", err);
      setSnackbar({
        open: true,
        message: `Failed to delete customer: ${err.message || "Unknown error"}`,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  function generateUniqueCustomerId() {
    const timestamp = Date.now().toString(36);
    const randomChars = Math.random().toString(36).substring(2, 7);
    return `CUS-${timestamp}-${randomChars}`.toUpperCase();
  }

  // Show loading state
  if (loading) {
    return (
      <Layout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="70vh"
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Paper style={{ padding: 16, margin: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Customer Management</Typography>
          
          {selectedCompanyId && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              style={{
                color: "white",
                backgroundColor: "#f44336",
              }}
            >
              Add New Customer
            </Button>
          )}
        </Box>

        {!selectedCompanyId ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            height="200px"
            border="1px dashed #ccc"
            borderRadius={2}
          >
            <Typography variant="h6" color="text.secondary">
              Please select a company to view customers
            </Typography>
          </Box>
        ) : Array.isArray(customers) && customers.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="200px"
            border="1px dashed #ccc"
            borderRadius={2}
          >
            <Typography variant="h6" color="text.secondary">
              Your customer list is empty
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: "60vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Segment</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(customers) && customers.map((customer) => (
                  <TableRow key={customer._id || customer.customerId}>
                    <TableCell>{customer.customerId}</TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>{customer.customerSegment}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleOpenDialog(customer)}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteCustomer(customer._id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedCustomer ? "Edit Customer" : "Add New Customer"}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ p: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Name"
                    name="name"
                    value={formData.name || ""}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email || ""}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    name="phone"
                    value={formData.phone || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Customer Segment</InputLabel>
                    <Select
                      name="customerSegment"
                      value={formData.customerSegment || "B2B"}
                      onChange={handleInputChange}
                      label="Customer Segment"
                    >
                      <MenuItem value="B2B">B2B</MenuItem>
                      <MenuItem value="B2C">B2C</MenuItem>
                      <MenuItem value="Enterprise">Enterprise</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                    Address Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="City"
                    name="address.city"
                    value={formData.address?.city || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State"
                    name="address.state"
                    value={formData.address?.state || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Postal Code"
                    name="address.postalCode"
                    value={formData.address?.postalCode || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                    Purchase Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Payment ID"
                    name="paymentId"
                    value={formData.paymentId || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Purchase Date"
                    name="purchaseDate"
                    type="date"
                    value={formData.purchaseDate || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Product Purchased"
                    name="productPurchased"
                    value={formData.productPurchased || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Amount Spent"
                    name="amountspend"
                    type="number"
                    value={formData.amountspend || ""}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Typography>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      name="paymentMethod"
                      value={formData.paymentMethod || ""}
                      onChange={handleInputChange}
                      label="Payment Method"
                    >
                      <MenuItem value="Credit Card">Credit Card</MenuItem>
                      <MenuItem value="Debit Card">Debit Card</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="Net Banking">Net Banking</MenuItem>
                      <MenuItem value="Cash">Cash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleClick} 
              variant="contained" 
              color="primary"
              disabled={!formData.name || !formData.email}
            >
              {selectedCustomer ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Layout>
  );
};

export default CustomerManagement;
